import React, { useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { func } from "prop-types";
import * as style from "./styles.module.scss";
import { postReflexAllenOnboardingForm } from "../../../redux/actions";
import {
  TextFieldGroup,
  TextAreaGroup,
  CheckboxButton,
  Checkbox,
} from "../../../common";
import { DOB } from "../../BasicInfo";

const NightWorkersQuestionnaire = ({ postForm }) => {
  const [birthDate, setBirthDate] = useState({
    birthDay: "",
    birthMonth: "",
    birthYear: "",
  });
  const [statementConfirmed, setStatementConfirmed] = useState(false);
  const handleDateChange = (fieldName, value) => {
    setBirthDate({ ...birthDate, [fieldName]: value });
  };
  const stringFields = [
    "employerName",
    "jobTitle",
    "name",
    "address",
    "telephoneNumber",
    "site",
    "department",
    "gpName",
    "gpAddress",
  ];
  const boolFields = [
    "sex",
    "nightShifts",
    "permNightShift",
    "isDiabetic",
    "heartCondition",
    "stomachCondition",
    "mealTiming",
    "sleepCondition",
    "chestDisorder",
    "requireMedication",
    "mentalDisorder",
    "otherHealthFactors",
  ];
  const [formObject, setFormObject] = useState({
    ...stringFields.reduce((obj, field) => ({ ...obj, [field]: "" }), {}),
    ...boolFields.reduce((obj, field) => ({ ...obj, [field]: undefined }), {}),
    nightShiftsDuration: "",
  });
  const handleSetFormObject = (key, value) => {
    setFormObject({ ...formObject, [key]: value });
  };
  const additionalFieldsNotFilled =
    (formObject.nightShifts && formObject.nightShiftsDuration === "") ||
    (formObject.isDiabetic &&
      formObject.requireInsulinInjections === undefined) ||
    (formObject.heartCondition && formObject.heartAffection === undefined);

  const isSubmitDisabled =
    stringFields.some((field) => !formObject[field]) ||
    boolFields.some((field) => formObject[field] === undefined) ||
    !birthDate.birthDay ||
    !birthDate.birthMonth ||
    !birthDate.birthYear ||
    !statementConfirmed ||
    additionalFieldsNotFilled;

  const handleSubmit = () => {
    postForm({
      online_form: "night-workers-health-questionnaire",
      data: {
        ...formObject,
        birthDate,
      },
    });
  };

  return (
    <div className={style.container}>
      <h2>Night Workers Health Questionnaire</h2>
      <div className={style.container__form_container}>
        <p className="text-center">
          This form is designed to help assess if you have any health condition,
          which could affect your ability to perform night work. The opportunity
          for an assessment is required by the Working Time Regulations 1998.
          This form asks specific questions about your health.
        </p>
        <p className="text-center">
          Please check the information you enter before submitting, as once
          submitted it can not be edited.
        </p>
        <b className="text-center">
          All fields marked with an asterisk* are required.
        </b>
        <div className={style.nightWorkersQuestionnaire__form}>
          <div className="form-group mt-1">
            <label htmlFor="employerName">Employer*</label>
            <TextFieldGroup
              inputId="employerName"
              inputName="employerName"
              value={formObject.employerName || ""}
              onChange={(e) =>
                handleSetFormObject("employerName", e.target.value)
              }
              classes={`register_field`}
              isBold
            />
          </div>
          <div className="form-group mt-1">
            <label htmlFor="jobTitle">Job title*</label>
            <TextFieldGroup
              inputId="jobTitle"
              inputName="jobTitle"
              value={formObject.jobTitle || ""}
              onChange={(e) => handleSetFormObject("jobTitle", e.target.value)}
              classes={`register_field`}
              isBold
            />
          </div>
          <div className="form-group mt-1">
            <label htmlFor="name">Name*</label>
            <TextFieldGroup
              inputId="name"
              inputName="name"
              value={formObject.name || ""}
              onChange={(e) => handleSetFormObject("name", e.target.value)}
              classes={`register_field`}
              isBold
            />
          </div>
          <div className="form-group mt-1">
            <label htmlFor="address">Address*</label>
            <TextAreaGroup
              inputId={"address"}
              classes={`register_field ${style.nightWorkersQuestionnaire__address}`}
              value={formObject.address}
              rows={7}
              onChange={(e) => {
                handleSetFormObject("address", e.target.value);
              }}
            />
          </div>
          <div className="form-group mt-1">
            <label htmlFor="telephoneNumber">Home telephone number*</label>
            <TextFieldGroup
              inputId="telephoneNumber"
              inputName="telephoneNumber"
              value={formObject.telephoneNumber || ""}
              onChange={(e) =>
                handleSetFormObject("telephoneNumber", e.target.value)
              }
              classes={`register_field`}
              isBold
            />
          </div>
          <div className="form-group">
            <label htmlFor="departmentName">Date of birth*</label>
            <div
              className={`form-group row justify-content-center d-flex ${style.nightWorkersQuestionnaire__date}`}
            >
              <DOB
                title={""}
                dayOfBirth={birthDate.days}
                monthOfBirth={birthDate.months}
                yearOfBirth={birthDate.year}
                handleChange={handleDateChange}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Sex*</label>
            <div className="btn-group clearfix d-block">
              <CheckboxButton
                isChecked={formObject.sex === "male"}
                onClick={() => handleSetFormObject("sex", "male")}
                label="Male"
                width="50%"
              />
              <CheckboxButton
                isChecked={formObject.sex === "female"}
                onClick={() => handleSetFormObject("sex", "female")}
                label="Female"
                width="50%"
              />
            </div>
          </div>
          <div className={`form-group mt-1`}>
            <label htmlFor="site">Site*</label>
            <TextFieldGroup
              inputId="site"
              inputName="site"
              value={formObject.site || ""}
              onChange={(e) => handleSetFormObject("site", e.target.value)}
              classes={`register_field`}
              isBold
            />
          </div>
          <div className="form-group mt-1">
            <label htmlFor="department">Department*</label>
            <TextFieldGroup
              inputId="department"
              inputName="department"
              value={formObject.department || ""}
              onChange={(e) =>
                handleSetFormObject("department", e.target.value)
              }
              classes={`register_field`}
              isBold
            />
          </div>
          <div className="form-group mt-1">
            <label htmlFor="gpName">GP name*</label>
            <TextFieldGroup
              inputId="gpName"
              inputName="gpName"
              value={formObject.gpName || ""}
              onChange={(e) => handleSetFormObject("gpName", e.target.value)}
              classes={`register_field`}
              isBold
            />
          </div>
          <div className="form-group mt-1">
            <label htmlFor="gpAddress">GP address*</label>
            <TextAreaGroup
              inputId={"gpAddress"}
              classes={`register_field ${style.nightWorkersQuestionnaire__address}`}
              value={formObject.gpAddress}
              rows={7}
              onChange={(e) => {
                handleSetFormObject("gpAddress", e.target.value);
              }}
            />
          </div>
        </div>
        <p className="text-center">
          Please complete this form to the best of your knowledge and select the
          appropriate option.
        </p>
        <p className="text-center">
          Please note that selecting “yes” does not necessarily mean you are
          unfit for night work (simply that you need to be referred for further
          assessment).
        </p>
        <div className={style.nightWorkersQuestionnaire__form}>
          <div className="form-group">
            <label>Have you worked night shifts?*</label>
            <div className="btn-group clearfix d-block">
              <CheckboxButton
                isChecked={formObject.nightShifts}
                onClick={() => handleSetFormObject("nightShifts", true)}
                label="Yes"
                width="50%"
              />
              <CheckboxButton
                isChecked={formObject.nightShifts === false}
                onClick={() => {
                  setFormObject({
                    ...formObject,
                    nightShifts: false,
                    nightShiftsDuration: "",
                  });
                }}
                label="No"
                width="50%"
              />
            </div>
          </div>
          {formObject.nightShifts && (
            <div className="form-group mt-1">
              <label htmlFor="nightShiftsDuration">For how long?*</label>
              <TextFieldGroup
                inputId="nightShiftsDuration"
                inputName="nightShiftsDuration"
                value={formObject.nightShiftsDuration}
                defaultValue=""
                onChange={(e) =>
                  handleSetFormObject("nightShiftsDuration", e.target.value)
                }
                classes={`register_field`}
                isBold
              />
            </div>
          )}
          <div className="form-group">
            <label>Are you on permanent night shift?*</label>
            <div className="btn-group clearfix d-block">
              <CheckboxButton
                isChecked={formObject.permNightShift}
                onClick={() => handleSetFormObject("permNightShift", true)}
                label="Yes"
                width="50%"
              />
              <CheckboxButton
                isChecked={formObject.permNightShift === false}
                onClick={() => handleSetFormObject("permNightShift", false)}
                label="No"
                width="50%"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Do you suffer from diabetes?*</label>
            <div className="btn-group clearfix d-block">
              <CheckboxButton
                isChecked={formObject.isDiabetic}
                onClick={() => handleSetFormObject("isDiabetic", true)}
                label="Yes"
                width="50%"
              />
              <CheckboxButton
                isChecked={formObject.isDiabetic === false}
                onClick={() => {
                  setFormObject({
                    ...formObject,
                    isDiabetic: false,
                    requireInsulinInjections: undefined,
                  });
                }}
                label="No"
                width="50%"
              />
            </div>
          </div>
          {formObject.isDiabetic && (
            <div className="form-group">
              <label>Do you require insulin injections?*</label>
              <div className="btn-group clearfix d-block">
                <CheckboxButton
                  isChecked={formObject.requireInsulinInjections}
                  onClick={() =>
                    handleSetFormObject("requireInsulinInjections", true)
                  }
                  label="Yes"
                  width="50%"
                />
                <CheckboxButton
                  isChecked={formObject.requireInsulinInjections === false}
                  onClick={() =>
                    handleSetFormObject("requireInsulinInjections", false)
                  }
                  label="No"
                  width="50%"
                />
              </div>
            </div>
          )}
          <div className="form-group">
            <label>
              Do you suffer from a heart condition or a circulatory disorder?*
            </label>
            <div className="btn-group clearfix d-block">
              <CheckboxButton
                isChecked={formObject.heartCondition}
                onClick={() => handleSetFormObject("heartCondition", true)}
                label="Yes"
                width="50%"
              />
              <CheckboxButton
                isChecked={formObject.heartCondition === false}
                onClick={() => {
                  setFormObject({
                    ...formObject,
                    heartCondition: false,
                    heartAffection: undefined,
                  });
                }}
                label="No"
                width="50%"
              />
            </div>
          </div>
          {formObject.heartCondition && (
            <div className="form-group">
              <label>
                Does this affect your physical stamina and your ability to do
                physical work?*
              </label>
              <div className="btn-group clearfix d-block">
                <CheckboxButton
                  isChecked={formObject.heartAffection}
                  onClick={() => handleSetFormObject("heartAffection", true)}
                  label="Yes"
                  width="50%"
                />
                <CheckboxButton
                  isChecked={formObject.heartAffection === false}
                  onClick={() => handleSetFormObject("heartAffection", false)}
                  label="No"
                  width="50%"
                />
              </div>
            </div>
          )}
          <div className="form-group">
            <label>
              Do you suffer from any stomach or intestinal disorder such as
              peptic ulcers or duodenal ulcers?*
            </label>
            <div className="btn-group clearfix d-block">
              <CheckboxButton
                isChecked={formObject.stomachCondition}
                onClick={() => handleSetFormObject("stomachCondition", true)}
                label="Yes"
                width="50%"
              />
              <CheckboxButton
                isChecked={formObject.stomachCondition === false}
                onClick={() => handleSetFormObject("stomachCondition", false)}
                label="No"
                width="50%"
              />
            </div>
          </div>
          <div className="form-group">
            <label>
              Do you have any condition where the timing of a meal is
              particularly important?*
            </label>
            <div className="btn-group clearfix d-block">
              <CheckboxButton
                isChecked={formObject.mealTiming}
                onClick={() => handleSetFormObject("mealTiming", true)}
                label="Yes"
                width="50%"
              />
              <CheckboxButton
                isChecked={formObject.mealTiming === false}
                onClick={() => handleSetFormObject("mealTiming", false)}
                label="No"
                width="50%"
              />
            </div>
          </div>
          <div className="form-group">
            <label>
              Do you suffer from any (medical) condition affecting your sleep?*
            </label>
            <div className="btn-group clearfix d-block">
              <CheckboxButton
                isChecked={formObject.sleepCondition}
                onClick={() => handleSetFormObject("sleepCondition", true)}
                label="Yes"
                width="50%"
              />
              <CheckboxButton
                isChecked={formObject.sleepCondition === false}
                onClick={() => handleSetFormObject("sleepCondition", false)}
                label="No"
                width="50%"
              />
            </div>
          </div>
          <div className="form-group">
            <label>
              Do you suffer from a chronic chest disorder (such as asthma) where
              night-time symptoms are particularly troublesome?*
            </label>
            <div className="btn-group clearfix d-block">
              <CheckboxButton
                isChecked={formObject.chestDisorder}
                onClick={() => handleSetFormObject("chestDisorder", true)}
                label="Yes"
                width="50%"
              />
              <CheckboxButton
                isChecked={formObject.chestDisorder === false}
                onClick={() => handleSetFormObject("chestDisorder", false)}
                label="No"
                width="50%"
              />
            </div>
          </div>
          <div className="form-group">
            <label>
              Do you suffer from any medical condition requiring regular
              Medication at strict times e.g. epilepsy or thyroid disease?*
            </label>
            <div className="btn-group clearfix d-block">
              <CheckboxButton
                isChecked={formObject.requireMedication}
                onClick={() => handleSetFormObject("requireMedication", true)}
                label="Yes"
                width="50%"
              />
              <CheckboxButton
                isChecked={formObject.requireMedication === false}
                onClick={() => handleSetFormObject("requireMedication", false)}
                label="No"
                width="50%"
              />
            </div>
          </div>
          <div className="form-group">
            <label>
              Have you had depression, “stress”, nervous disorder or other
              mental illness, alcohol or drug addiction?*
            </label>
            <div className="btn-group clearfix d-block">
              <CheckboxButton
                isChecked={formObject.mentalDisorder}
                onClick={() => handleSetFormObject("mentalDisorder", true)}
                label="Yes"
                width="50%"
              />
              <CheckboxButton
                isChecked={formObject.mentalDisorder === false}
                onClick={() => handleSetFormObject("mentalDisorder", false)}
                label="No"
                width="50%"
              />
            </div>
          </div>
          <div className="form-group">
            <label>
              Are you aware of any other health factors that may affect your
              fitness to do night work or do you feel night shifts affect your
              health in any way?*
            </label>
            <div className="btn-group clearfix d-block">
              <CheckboxButton
                isChecked={formObject.otherHealthFactors}
                onClick={() => handleSetFormObject("otherHealthFactors", true)}
                label="Yes"
                width="50%"
              />
              <CheckboxButton
                isChecked={formObject.otherHealthFactors === false}
                onClick={() => handleSetFormObject("otherHealthFactors", false)}
                label="No"
                width="50%"
              />
            </div>
          </div>
          <div className="form-group mt-1">
            <label htmlFor="additionalComments">
              Do you have any additional comments?
            </label>
            <TextAreaGroup
              inputId={"additionalComments"}
              classes="register_field"
              value={formObject.additionalComments}
              rows={7}
              onChange={(e) => {
                handleSetFormObject("additionalComments", e.target.value);
              }}
            />
          </div>
        </div>
        <div>
          <div className={style.nightWorkersQuestionnaire__declaration}>
            <b className="text-center">Declaration:</b>
            <p className="text-center">
              I certify that the answers to the above questions are correct to
              the best of my knowledge and belief. I understand that if I have
              withheld information, this may adversely affect efforts to place
              me in suitable employment.
            </p>
            <Checkbox
              label="I confirm the above statement"
              isChecked={statementConfirmed}
              onClick={() => setStatementConfirmed(!statementConfirmed)}
            />
          </div>
          <div className="d-flex flex-column flex-md-row-reverse mt-2">
            <Link to="/profile/edit/onboarding">
              <button
                className="btn btn-primary w-100"
                onClick={handleSubmit}
                disabled={isSubmitDisabled}
              >
                Save & submit
              </button>
            </Link>
            <Link
              to="/profile/edit/onboarding"
              className="btn btn-outline-dark mr-md-2 mt-1 mt-md-0"
            >
              Cancel
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

NightWorkersQuestionnaire.propTypes = {
  postForm: func,
};

const mapStateToProps = (state) => ({
  reflexAllenOnboarding: state.core.app.reflexAllen,
});

const mapDispatchToProps = {
  postForm: postReflexAllenOnboardingForm,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(NightWorkersQuestionnaire);
